import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Totals of a month's sales; all amounts in cents. */
export interface StatementTotals {
  count: number;
  face_value_cents: number;
  /** What customers paid for the vouchers (after discounts, without Schenkwerk's service fee). */
  paid_cents: number;
  fee_cents: number;
  /** The customer service fee the shop took over. */
  covered_customer_fee_cents: number;
  total_fee_cents: number;
  /** paid - fees: what was transferred to the shop. */
  payout_cents: number;
}

export interface MonthlyStatement {
  period: string;
  period_label: string;
  /** False for the running month: more sales can still come in. */
  is_complete: boolean;
  company_public_id: string;
  company_name: string | null;
  /** Number of the month's fee invoice; null when nothing was billed. */
  fee_invoice_number: string | null;
  totals: StatementTotals;
  refunds_total_cents: number;
  redemptions_total_cents: number;
  internal_issued_count: number;
  internal_issued_value_cents: number;
  /** Payouts that reached the bank in the month (they can hold last month's sales). */
  payouts: PayoutSummary[];
  payouts_total_cents: number;
}

/** How far a payout is broken down into the vouchers it paid out. */
export type PayoutReconciliation = 'pending' | 'complete' | 'incomplete' | 'unavailable';

/** A payout from the shop's Stripe account to its bank, as on the bank statement. */
export interface PayoutSummary {
  stripe_payout_id: string;
  /** YYYY-MM-DD: the day it reaches (or reached) the bank. */
  arrival_date: string;
  created_at: string;
  amount_cents: number;
  currency: string;
  /** Stripe's status: pending, in_transit, paid, failed, canceled. */
  status: string;
  status_label: string;
  reconciliation: PayoutReconciliation;
  bank_name: string | null;
  bank_last4: string | null;
  statement_descriptor: string | null;
  voucher_count: number;
}

export interface Download {
  blob: Blob;
  filename: string;
}

/** Hand a downloaded file to the browser, named like the server suggested. */
export function saveDownload(download: Download, fallbackName: string): void {
  const url = URL.createObjectURL(download.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = download.filename !== 'download' ? download.filename : fallbackName;
  link.click();
  URL.revokeObjectURL(url);
}

/** The API's error detail of a failed download - blob responses carry the JSON as a blob. */
export async function downloadErrorMessage(err: any): Promise<string> {
  try {
    const detail =
      err?.error instanceof Blob ? JSON.parse(await err.error.text()).detail : err?.error?.detail;
    if (detail) return detail;
  } catch {
    // not JSON: fall through to the generic message
  }
  return 'Der Download hat nicht geklappt. Bitte versuche es erneut.';
}

/** The shop's bookkeeping documents: monthly statement of sales, fees and payouts. */
@Injectable({ providedIn: 'root' })
export class AccountingService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  getMonthlyStatement(period: string): Observable<MonthlyStatement> {
    return this.http.get<MonthlyStatement>(`${this.apiUrl}/accounting/monthly-statement`, {
      params: { period },
    });
  }

  downloadMonthlyStatement(period: string, format: 'pdf' | 'csv'): Observable<Download> {
    return this.download(`${this.apiUrl}/accounting/monthly-statement`, { period, format });
  }

  listPayouts(year?: number): Observable<PayoutSummary[]> {
    const params: Record<string, string> = year ? { year: String(year) } : {};
    return this.http.get<PayoutSummary[]>(`${this.apiUrl}/accounting/payouts`, { params });
  }

  downloadPayoutStatement(stripePayoutId: string, format: 'pdf' | 'csv'): Observable<Download> {
    return this.download(
      `${this.apiUrl}/accounting/payouts/${encodeURIComponent(stripePayoutId)}`,
      { format },
    );
  }

  private download(url: string, params: Record<string, string>): Observable<Download> {
    return this.http.get(url, { params, responseType: 'blob', observe: 'response' }).pipe(
      map((res) => {
        const disposition = res.headers.get('Content-Disposition') ?? '';
        const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'download';
        return { blob: res.body as Blob, filename };
      }),
    );
  }
}
